'use client';

import { CreditCard } from '@phosphor-icons/react';
import { LegalPageLayout } from '@/components/legal/legal-page-layout';

const BulletList = ({ items }: { items: string[] }) => (
  <ul className="space-y-1.5 pl-1">
    {items.map((item, i) => (
      <li key={i} className="flex items-start gap-2">
        <span className="mt-1.5 w-1 h-1 rounded-pill bg-[var(--primary)] flex-shrink-0" />
        <span>{item}</span>
      </li>
    ))}
  </ul>
);

const Highlight = ({ children }: { children: React.ReactNode }) => (
  <div className="p-4 rounded-card bg-[var(--primary-subtle)] text-[var(--text-primary)] text-sm">
    {children}
  </div>
);

export function PaymentContent() {
  const sections = [
    {
      title: 'Оплата Online',
      content: (
        <p>
          Вы можете приобрести любой товар на сайте с помощью онлайн-оплаты.
        </p>
      ),
    },
    {
      // Раньше в описании процедуры стояло «выбрать один из способов оплаты» —
      // выбора нет: способ один, онлайн-оплата банковской картой через форму
      // банка (components/features/cart/steps/payment-step.tsx). Наличные и
      // оплата курьеру не принимаются (вопрос 47), поэтому «один из способов»
      // обещал несуществующую развилку.
      title: 'Процедура заказа товара',
      content: (
        <>
          <p>
            Для того чтобы совершить заказ, нажмите на кнопку «В корзину», и выбранный товар попадёт в «Корзину».
            Затем, в «Корзине», проверьте сформированный заказ и воспользуйтесь формой «Оформления заказа».
            Необходимо будет внести свои личные данные и оплатить заказ банковской картой онлайн.
          </p>
          <p>
            Если у вас возникли вопросы по порядку заказа товара, мы будем рады ответить по телефону{' '}
            <a href="tel:+78633335050" className="text-[var(--primary)] font-medium hover:underline">+7 (863) 333-50-50</a>{' '}
            или по электронной почте{' '}
            <a href="mailto:aziabox@ingox.ru" className="text-[var(--primary)] font-medium hover:underline">aziabox@ingox.ru</a>.
          </p>
        </>
      ),
    },
    {
      title: 'Оплата банковской картой',
      content: (
        <>
          <p>
            При выборе формы оплаты с помощью пластиковой карты проведение платежа производится в процессе оформления заказа.
          </p>
          <p>Оплата происходит через авторизационный сервер процессингового центра банка с использованием карт следующих платёжных систем:</p>
          <BulletList items={['VISA International', 'MasterCard Worldwide', 'МИР']} />
          <p className="mt-3">
            Система переключит вас на страницу сервера, где вам будет предложено ввести данные карты, инициировать её авторизацию, после чего вернуться в наш магазин.
            В случае подтверждения авторизации ваш заказ будет автоматически выполняться. В случае отказа вы сможете повторить процедуру оплаты.
          </p>
        </>
      ),
    },
    {
      title: 'Конфиденциальность',
      content: (
        <>
          <Highlight>
            <p>Ввод данных банковской карты осуществляется на защищённой платёжной странице банка с использованием протокола SSL 3.0. Информация передаётся в зашифрованном виде.</p>
          </Highlight>
          <p className="font-medium text-[var(--text-primary)] mt-4">Случаи отказа в совершении платежа:</p>
          <BulletList items={[
            'Банковская карта не предназначена для совершения платежей через интернет',
            'Недостаточно средств для оплаты на банковской карте',
            'Данные банковской карты введены неверно',
            'Истёк срок действия банковской карты',
            'Превышен установленный лимит операций за день',
          ]} />
          <p className="font-medium text-[var(--text-primary)] mt-4">Для корректного ввода данных карты:</p>
          <ol className="space-y-2 pl-1 mt-2">
            {[
              'Владелец карты — латинскими буквами, как указано на карте',
              'Номер карты — 16 цифр на лицевой стороне, без пробелов',
              'Срок действия — месяц и год',
              'CVV2/CVC2 — 3 цифры на обратной стороне карты',
            ].map((item, i) => (
              <li key={i} className="flex items-start gap-2.5">
                <span className="w-5 h-5 rounded-card bg-[var(--primary-subtle)] text-[var(--primary)] text-xs font-bold flex items-center justify-center flex-shrink-0 mt-0.5">{i + 1}</span>
                <span>{item}</span>
              </li>
            ))}
          </ol>
        </>
      ),
    },
    {
      title: 'Возврат денежных средств',
      content: (
        <>
          <p>Возврат наличными денежными средствами при онлайн-оплате не допускается. Порядок возврата регулируется правилами международных платёжных систем.</p>
          <p>
            Для возврата денежных средств на банковскую карту обратитесь в call-центр по телефону{' '}
            <a href="tel:+78633335050" className="text-[var(--primary)] font-medium hover:underline">+7 (863) 333-50-50</a>{' '}
            и предоставьте номер карты, с которой производилась оплата.
          </p>
          <Highlight>
            <p>Возврат денежных средств будет осуществлён на банковский счёт в течение 21 рабочего дня со дня получения заявки.</p>
          </Highlight>
        </>
      ),
    },
    {
      title: 'Реквизиты компании',
      content: (
        <div className="space-y-3">
          <p className="font-medium text-[var(--text-primary)]">ИП Хомич Анастасия Алексеевна</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm">
            <div><span className="text-[var(--text-muted)]">ОГРНИП:</span> 3259400100013071</div>
            <div><span className="text-[var(--text-muted)]">ИНН:</span> 345951074341</div>
            <div className="sm:col-span-2"><span className="text-[var(--text-muted)]">Р/с:</span> 40802810320000574759</div>
            <div><span className="text-[var(--text-muted)]">Банк:</span> ООО «Банк Точка»</div>
            <div><span className="text-[var(--text-muted)]">БИК:</span> 044525104</div>
            <div className="sm:col-span-2"><span className="text-[var(--text-muted)]">Корр. счёт:</span> 30101810745374525104</div>
          </div>
        </div>
      ),
    },
  ];

  return (
    <LegalPageLayout
      title="Оплата"
      icon={<CreditCard className="w-3.5 h-3.5 text-[var(--primary)]" weight="fill" />}
      sections={sections}
      breadcrumbs={[
        { name: 'Главная', href: '/' },
        { name: 'Оплата', href: '/payment' },
      ]}
      contactBlock={
        <div className="text-sm text-[var(--text-secondary)]">
          <p>Вопросы по оплате:</p>
          <a href="tel:+78633335050" className="text-[var(--primary)] font-medium hover:underline">+7 (863) 333-50-50</a>
          {' · '}
          <a href="mailto:aziabox@ingox.ru" className="text-[var(--primary)] font-medium hover:underline">aziabox@ingox.ru</a>
        </div>
      }
    />
  );
}
